import React, { useState, useCallback, useMemo } from 'react'
import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { lighten } from '@mui/material/styles'
import {
  PencilIcon,
  XIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronsDownUpIcon,
  ChevronsUpDownIcon,
  RotateCcwIcon,
  InfoIcon,
} from 'lucide-react'
import { Button, Switch } from '@repo/ui'
import { Loader, ResetRoleModal } from 'components/feedback'
import { Search } from 'components/input'
import { SelectField } from 'components'
// Sibling path, not the `components` barrel: this file is exported FROM that barrel,
// so going through it would be a cycle — same reason ExampleKindBadge reaches it by path.
import { InfoTooltip } from '../../feedback/InfoTooltip'
import { useColorMode } from 'hooks'

import {
  useRolesMatrix,
  useUpdateRolePermissions,
  useCreateRoleOverride,
  useInternalRolesMatrix,
  useUpdateInternalRolePermissions,
  MatrixRoleItem,
  PermissionGroup,
  PermissionMeta,
  PermissionOperation,
} from 'hooks/api'
import { getRoleDisplayName, getRoleStatus } from 'constants/roles'
import { ROLE_STATUSES } from 'constants/statuses'
import { vhMinus } from 'utils/textZoom'

// ── Props ─────────────────────────────────────────────────────────────────────

interface PermissionsMatrixProps {
  /**
   * When set, restrict the matrix to a single role column (used by RolePage).
   * The role must still be loaded from the matrix endpoint for permission data.
   */
  singleRoleId?: string
  /**
   * When true, suppress the Search input in the toolbar so the narrow
   * single-role card on RolePage isn't cluttered. The Edit/Save/Cancel buttons
   * remain right-aligned. Defaults to false (search visible in full matrix).
   */
  hideSearch?: boolean
  /**
   * 'company' (default) — shows the company roles matrix (existing behaviour,
   * unchanged). 'internal' — shows the Vera-staff internal roles matrix using
   * the internal hooks + endpoints. Existing company usages omit this prop and
   * are completely unaffected.
   */
  scope?: 'company' | 'internal'
}

// ── Local state for dirty tracking ────────────────────────────────────────────
/**
 * Map: customRoleId → Set<permissionName>
 * Tracks the in-edit permission set for each custom role.
 * Only populated while editMode === true.
 */
type LocalPermsState = Record<string, Set<string>>

// Operation filter (CRUD + Action) — a multi-select rendered with the shared
// SelectField dropdown (the same control the Product edit page's Type field uses):
// a row list with a right-aligned check on each selected row.
const OP_FILTER_ITEMS: { label: string; value: PermissionOperation }[] = [
  { label: 'Read', value: 'read' },
  { label: 'Create', value: 'create' },
  { label: 'Update', value: 'update' },
  { label: 'Delete', value: 'delete' },
  { label: 'Action', value: 'action' },
]

// ── Component ─────────────────────────────────────────────────────────────────

export const PermissionsMatrix = ({ singleRoleId, hideSearch = false, scope = 'company' }: PermissionsMatrixProps) => {
  const theme = useTheme()
  const { resolvedMode } = useColorMode()
  const isDark = resolvedMode === 'dark'
  const isInternal = scope === 'internal'

  // Company matrix hooks (only active when scope === 'company')
  const companyMatrix = useRolesMatrix()
  const updatePermissions = useUpdateRolePermissions()
  const createOverride = useCreateRoleOverride()

  // Internal matrix hooks (only active when scope === 'internal')
  const internalMatrix = useInternalRolesMatrix()
  const updateInternalPermissions = useUpdateInternalRolePermissions()

  // Unified data/loading/refetch based on scope
  const { data, isLoading, refetch } = isInternal ? internalMatrix : companyMatrix

  const [editMode, setEditMode] = useState(false)
  const [localPerms, setLocalPerms] = useState<LocalPermsState>({})
  const [search, setSearch] = useState('')
  // Operation filter (CRUD + Action), multi-select. Empty = no filter.
  const [opFilters, setOpFilters] = useState<PermissionOperation[]>([])
  // The role whose override is being reset (edit-mode, modified-built-in columns).
  const [resetRole, setResetRole] = useState<MatrixRoleItem | null>(null)
  // Collapsed permission groups (by group.key). All expanded by default; the
  // group header (with its per-role bulk toggles) stays visible when collapsed,
  // so a whole category can still be turned on/off without expanding.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())

  const toggleGroupCollapse = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  // ── Derived data ───────────────────────────────────────────────────────────

  const allRoles = data?.roles ?? []
  const groups = data?.groups ?? []

  // Filter permission rows by name AND by operation (CRUD + Action); drop groups
  // left with no matches.
  const visibleGroups = useMemo((): PermissionGroup[] => {
    const q = search.trim().toLowerCase()
    if (!q && opFilters.length === 0) return groups
    return groups
      .map((g: PermissionGroup) => ({
        ...g,
        permissions: g.permissions.filter(
          (p: PermissionMeta) =>
            (!q || p.label.toLowerCase().includes(q)) &&
            (opFilters.length === 0 || (p.operation && opFilters.includes(p.operation))),
        ),
      }))
      .filter((g: PermissionGroup) => g.permissions.length > 0)
  }, [groups, search, opFilters])

  const displayRoles = useMemo((): MatrixRoleItem[] => {
    if (singleRoleId) return allRoles.filter((r: MatrixRoleItem) => r.id === singleRoleId)
    return allRoles
  }, [allRoles, singleRoleId])

  /**
   * Build a lookup: permissionName → permissionId
   * Sourced from ALL roles in the matrix (we need this to resolve IDs when saving).
   * Names are globally unique so first-seen wins.
   */
  const nameToId = useMemo((): Record<string, string> => {
    const map: Record<string, string> = {}
    for (const role of allRoles) {
      role.permissionNames.forEach((name: string, idx: number) => {
        if (!map[name] && role.permissionIds[idx]) {
          map[name] = role.permissionIds[idx]
        }
      })
    }
    return map
  }, [allRoles])

  /** True when any role's permission set has been changed in edit mode vs server state. */
  const isDirty = useMemo(() => {
    if (!data || Object.keys(localPerms).length === 0) return false
    return data.roles.some((role: MatrixRoleItem) => {
      const local = localPerms[role.id]
      if (!local) return false
      const serverSet = new Set(role.permissionNames)
      if (serverSet.size !== local.size) return true
      for (const name of Array.from(local)) {
        if (!serverSet.has(name)) return true
      }
      return false
    })
  }, [data, localPerms])

  // ── Handlers ───────────────────────────────────────────────────────────────

  const enterEditMode = useCallback(() => {
    if (!data) return
    const initial: LocalPermsState = {}
    data.roles.forEach((role: MatrixRoleItem) => {
      initial[role.id] = new Set(role.permissionNames)
    })
    setLocalPerms(initial)
    setEditMode(true)
  }, [data])

  const cancelEdit = useCallback(() => {
    setLocalPerms({})
    setEditMode(false)
  }, [])

  const handleToggle = useCallback(
    (role: MatrixRoleItem, permName: string) => {
      if (!editMode) return
      setLocalPerms((prev) => {
        const current = new Set(prev[role.id] ?? [])
        if (current.has(permName)) {
          current.delete(permName)
        } else {
          current.add(permName)
        }
        return { ...prev, [role.id]: current }
      })
    },
    [editMode],
  )

  const handleSave = useCallback(async () => {
    if (!data) return

    // Find ALL roles whose permission set changed vs server state
    const changedRoles = data.roles.filter((role: MatrixRoleItem) => {
      const local = localPerms[role.id]
      if (!local) return false
      const serverSet = new Set(role.permissionNames)
      if (serverSet.size !== local.size) return true
      for (const name of Array.from(local)) {
        if (!serverSet.has(name)) return true
      }
      return false
    })

    if (changedRoles.length === 0) {
      setEditMode(false)
      return
    }

    try {
      await Promise.all(
        changedRoles.map((role: MatrixRoleItem) => {
          // Resolve names → IDs using the nameToId lookup built from matrix data
          const permissionIds = Array.from(localPerms[role.id] ?? [])
            .map((name) => nameToId[name])
            .filter(Boolean)

          if (isInternal) {
            // Internal roles: write directly to global role_permissions via PUT /roles/internal/:id/permissions
            return updateInternalPermissions.mutateAsync({ roleId: role.id, permissionIds })
          }

          // Company roles: unmodified built-in → create/replace an override
          if (role.builtinRoleId && !role.isOverridden && !role.isCustom) {
            return createOverride.mutateAsync({ roleId: role.builtinRoleId, permissionIds })
          }
          // Override or standalone custom: update via the existing PUT endpoint
          return updatePermissions.mutateAsync({ id: role.id, permissionIds })
        }),
      )
      setLocalPerms({})
      setEditMode(false)
      refetch()
    } catch {
      // Error notification already fired by the respective mutation hooks
    }
  }, [data, localPerms, nameToId, updatePermissions, createOverride, refetch])

  // ── Permission state resolver ──────────────────────────────────────────────

  const isPermOn = useCallback(
    (role: MatrixRoleItem, permName: string): boolean => {
      if (editMode) {
        return localPerms[role.id]?.has(permName) ?? false
      }
      return role.permissionNames.includes(permName)
    },
    [editMode, localPerms],
  )

  /** Group-level state for a role: all on, or partially on (indeterminate). */
  const groupState = useCallback(
    (role: MatrixRoleItem, group: PermissionGroup): { allOn: boolean; partial: boolean } => {
      const allOn = group.permissions.every((p: PermissionMeta) => isPermOn(role, p.name))
      const someOn = group.permissions.some((p: PermissionMeta) => isPermOn(role, p.name))
      return { allOn, partial: someOn && !allOn }
    },
    [isPermOn],
  )

  /** Toggle every permission in a group on/off for one role (edit mode only). */
  const toggleGroup = useCallback(
    (role: MatrixRoleItem, group: PermissionGroup) => {
      if (!editMode) return
      const allOn = group.permissions.every((p: PermissionMeta) => isPermOn(role, p.name))
      setLocalPerms((prev) => {
        const current = new Set(prev[role.id] ?? [])
        group.permissions.forEach((p: PermissionMeta) => {
          if (allOn) current.delete(p.name)
          else current.add(p.name)
        })
        return { ...prev, [role.id]: current }
      })
    },
    [editMode, isPermOn],
  )

  /** Whether EVERY permission in the whole catalog is on for a role. */
  const allPermsOn = useCallback(
    (role: MatrixRoleItem): boolean => {
      const perms = groups.flatMap((g: PermissionGroup) => g.permissions)
      return perms.length > 0 && perms.every((p: PermissionMeta) => isPermOn(role, p.name))
    },
    [isPermOn, groups],
  )

  /**
   * Top-level Select all / Deselect all for one role (edit mode only): grant or
   * revoke EVERY permission across all groups in one click. A company sets its
   * Vera-Manager role to "everything" or "nothing" this way (nothing = no access).
   */
  const toggleAllPerms = useCallback(
    (role: MatrixRoleItem) => {
      if (!editMode) return
      const perms = groups.flatMap((g: PermissionGroup) => g.permissions)
      const on = perms.every((p: PermissionMeta) => isPermOn(role, p.name))
      setLocalPerms((prev) => {
        const current = new Set(prev[role.id] ?? [])
        perms.forEach((p: PermissionMeta) => {
          if (on) current.delete(p.name)
          else current.add(p.name)
        })
        return { ...prev, [role.id]: current }
      })
    },
    [editMode, isPermOn, groups],
  )

  // ── Render ─────────────────────────────────────────────────────────────────

  if (isLoading) return <Loader />
  if (!data || displayRoles.length === 0) return null

  const ROLE_COL_WIDTH = 150
  // Fixed (not 1fr) so the permission column stays wide enough for every label to
  // sit on one line regardless of how many role columns there are (the matrix
  // scrolls horizontally via the parent's overflowX). Permission names are kept
  // short by convention so they fit this width.
  const PERM_COL_WIDTH = 300
  const gridTemplate = `${PERM_COL_WIDTH}px ${displayRoles.map(() => `${ROLE_COL_WIDTH}px`).join(' ')}`

  // Row metrics — match the shared data Table (rowHeight 72 / header 44) so the
  // matrix reads with the same density as every other list surface, rather than
  // the cramped ~43px it had before.
  const HEADER_HEIGHT = 56 // header carries two lines (role name + Default/Custom)
  const GROUP_HEADER_HEIGHT = 40
  const ROW_HEIGHT = 72
  // Frozen-column boundary: a 1px divider on the right edge of the sticky
  // permission column so scrolled role columns visibly pass *under* it (same
  // pinned-divider affordance the shared Table gives its pinned columns).
  const PINNED_BORDER = `1px solid ${theme.palette.divider}`
  // Hide the scrollbars while keeping the matrix scrollable (mirrors the shared
  // Table, which suppresses its scrollbars too).
  const HIDE_SCROLLBARS = {
    scrollbarWidth: 'none' as const,
    msOverflowStyle: 'none' as const,
    '&::-webkit-scrollbar': { display: 'none', width: 0, height: 0 },
  }

  // Collapse-all / expand-all (the toggle in the top "Permission" header cell):
  // all visible groups collapsed → expand all; otherwise → collapse all.
  const allCollapsed =
    visibleGroups.length > 0 && visibleGroups.every((g) => collapsedGroups.has(g.key))
  const toggleAllGroups = () => {
    setCollapsedGroups(allCollapsed ? new Set() : new Set(visibleGroups.map((g) => g.key)))
  }

  return (
    <Box>
      {/* ── Search + Edit / Save / Cancel toolbar (stays put — does NOT scroll
          horizontally with the matrix) ─────────────────────────────────────── */}
      <Stack
        direction="row"
        justifyContent={hideSearch ? 'flex-end' : 'space-between'}
        alignItems="center"
        spacing={2}
        sx={{ pb: 5 }}
      >
        {/* Shared Search component (same as the Roles tab) for icon/size parity.
            Uncontrolled (no `value`): the Search owns its input state and pushes
            the debounced term out via onChange. Passing `value` back in fights
            the internal debounce and reverts each keystroke. */}
        {!hideSearch && <Search onChange={setSearch} />}
        <Stack direction="row" alignItems="center" spacing={2}>
        {/* Operation filter (CRUD + Action) — a multi-select dropdown that narrows
            the long list across all groups. Uses the shared SelectField (same
            control as the Product Type field). Hidden in single-role mode. */}
        {!hideSearch && (
          <Box sx={{ width: 200 }}>
            <SelectField
              multiple
              options={OP_FILTER_ITEMS}
              value={opFilters}
              onChange={(v) => setOpFilters(v as PermissionOperation[])}
              placeholder="All operations"
            />
          </Box>
        )}
        {/* Toolbar buttons match the dropdown + Search height (47px) so the whole
            toolbar row reads as one height. */}
        {!editMode ? (
          <Button
            variant="secondary"
            onClick={enterEditMode}
            startIcon={<PencilIcon size={16} strokeWidth={1.5} />}
            // Match the operation dropdown + Search field border (primary[100], 1px) so
            // the whole toolbar row shares one border width + colour.
            sx={{ height: 47, border: (t) => `1px solid ${t.palette.primary[100]}` }}
          >
            Edit
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={cancelEdit}
              startIcon={<XIcon size={16} strokeWidth={1.5} />}
              sx={{ height: 47, border: (t) => `1px solid ${t.palette.primary[100]}` }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!isDirty || updatePermissions.isPending}
              onClick={handleSave}
              startIcon={<CheckIcon size={16} strokeWidth={1.5} />}
              sx={{ height: 47 }}
            >
              Save
            </Button>
          </>
        )}
        </Stack>
      </Stack>

      {/* ── Matrix grid — ONLY this scrolls horizontally (the toolbar above
          stays fixed) when the role columns exceed the viewport ──────────────── */}
      <Box
        sx={{
          overflowX: 'auto',
          // Vertical scroll: the rows scroll while the column-header row stays
          // pinned (position: sticky, top: 0). Caps the matrix so the page itself
          // doesn't grow; sits ~24px from the viewport bottom (matches the gutter).
          overflowY: 'auto',
          maxHeight: vhMinus(290),
          // The bordered frame + rounded corners live on the SCROLL container so
          // it both clips the corners AND is the sticky scrollport: the first
          // permission column sticks relative to THIS element while the role
          // columns scroll. (An `overflow` on the inner grid would capture the
          // sticky positioning and break the freeze.)
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
          ...HIDE_SCROLLBARS,
        }}
      >
      <Box
        sx={{
          // Size to the columns so every role column renders at full width; the
          // scroll container above owns overflow. minWidth keeps it flush-width
          // when the columns fit without scrolling.
          width: 'fit-content',
          minWidth: '100%',
        }}
      >
        {/* Column headers: role names — pinned to the top while rows scroll */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: gridTemplate,
            minHeight: HEADER_HEIGHT,
            backgroundColor: theme.palette.primary[50],
            borderBottom: `1px solid ${theme.palette.divider}`,
            position: 'sticky',
            top: 0,
            zIndex: 3,
          }}
        >
          <Box
            sx={{
              px: 4,
              py: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              // Freeze the permission column while the role columns scroll right;
              // top-left corner stays above both the sticky header row and column.
              position: 'sticky',
              left: 0,
              zIndex: 4,
              backgroundColor: theme.palette.primary[50],
              borderRight: PINNED_BORDER,
            }}
          >
            {/* The matrix's own subject. A reader here is deciding what a role may do,
                which is the moment "permission" stops being a generic word and starts
                meaning one named capability granted only through a role. */}
            <Typography variant="body2" fontWeight={500}>
              Permission
              <InfoTooltip term="permission" />
            </Typography>
            {/* Collapse-all / expand-all every permission group at once. */}
            <Tooltip title={allCollapsed ? 'Expand all' : 'Collapse all'} placement="top">
              <Box
                component="button"
                type="button"
                onClick={toggleAllGroups}
                aria-label={allCollapsed ? 'Expand all groups' : 'Collapse all groups'}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1,
                  border: 'none',
                  borderRadius: 1,
                  cursor: 'pointer',
                  color: theme.palette.text.secondary,
                  backgroundColor: 'transparent',
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                }}
              >
                {allCollapsed ? (
                  <ChevronsUpDownIcon size={16} strokeWidth={2} />
                ) : (
                  <ChevronsDownUpIcon size={16} strokeWidth={2} />
                )}
              </Box>
            </Tooltip>
          </Box>
          {displayRoles.map((role) => {
            const status = getRoleStatus(role)
            // Colored TEXT (not a pill) in the matrix header — a row of 11 pills
            // read heavy and the lone pill-among-plain-text looked inconsistent.
            // Reuse the Roles-list pill COLORS (ROLE_STATUSES[status].color),
            // lightened in dark mode for legibility on the dark header, so the
            // two surfaces stay tied together by hue while the matrix stays light.
            const statusColor = isDark
              ? lighten(ROLE_STATUSES[status].color, 0.4)
              : ROLE_STATUSES[status].color
            return (
            <Box
              key={role.id}
              sx={{
                px: 2,
                py: 3,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                {getRoleDisplayName(role)}
                {/* VRG-677: the one column nobody is assigned to. Its meaning (what Vera
                    support may see inside this account, and that new companies start
                    with Ask Vera on) is not readable from its name. */}
                {role.name === 'vera_support' && <InfoTooltip term="vera-support-access" />}
              </Typography>
              <Typography
                variant="caption"
                display="block"
                sx={{ mt: 0.5, color: statusColor, fontWeight: 500 }}
              >
                {ROLE_STATUSES[status].label}
              </Typography>
              {/* Reset a Modified built-in back to the global default — edit mode
                  only, and only where there's an override to discard. */}
              {editMode && role.isOverridden && (
                <Button
                  variant="ghost"
                  startIcon={<RotateCcwIcon size={12} strokeWidth={1.5} />}
                  onClick={() => setResetRole(role)}
                  sx={{ mt: 0.5 }}
                >
                  Reset
                </Button>
              )}
              {/* Top-level grant/revoke everything for this role in one click
                  (edit mode). A company dials its Vera-Manager role from
                  "everything" to "nothing" (nothing = no access) from here. */}
              {editMode && (
                <Button
                  variant="ghost"
                  onClick={() => toggleAllPerms(role)}
                  sx={{ mt: 0.5, display: 'block', mx: 'auto' }}
                >
                  {allPermsOn(role) ? 'Deselect all' : 'Select all'}
                </Button>
              )}
            </Box>
            )
          })}
        </Box>

        {/* Permission groups and rows */}
        {visibleGroups.map((group: PermissionGroup, gIdx: number) => {
          const isCollapsed = collapsedGroups.has(group.key)
          return (
          <Box key={group.key}>
            {/* Group header */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: gridTemplate,
                minHeight: GROUP_HEADER_HEIGHT,
                backgroundColor: theme.palette.grey[50],
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box
                onClick={() => toggleGroupCollapse(group.key)}
                sx={{
                  px: 4,
                  py: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  cursor: 'pointer',
                  userSelect: 'none',
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  backgroundColor: theme.palette.grey[50],
                  borderRight: PINNED_BORDER,
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                }}
              >
                {isCollapsed ? (
                  <ChevronRightIcon size={14} strokeWidth={2} color={theme.palette.text.secondary} />
                ) : (
                  <ChevronDownIcon size={14} strokeWidth={2} color={theme.palette.text.secondary} />
                )}
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}
                >
                  {group.label}
                </Typography>
              </Box>
              {/* Per-column group toggle: turns every permission in this group
                  on/off for that role (edit mode only). */}
              {displayRoles.map((role) => {
                const { allOn, partial } = groupState(role, group)
                return (
                  <Box
                    key={role.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 1,
                    }}
                  >
                    <Switch
                      checked={partial ? true : allOn}
                      disabled={!editMode}
                      onChange={() => toggleGroup(role, group)}
                      size="small"
                      sx={{ opacity: partial ? 0.5 : editMode ? 1 : 0.6 }}
                    />
                  </Box>
                )
              })}
            </Box>

            {/* Individual permission rows (hidden while the group is collapsed) */}
            {!isCollapsed && group.permissions.map((perm: PermissionMeta, pIdx: number) => {
              const isLastRow =
                gIdx === visibleGroups.length - 1 &&
                pIdx === group.permissions.length - 1
              return (
                <Box
                  key={perm.name}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: gridTemplate,
                    minHeight: ROW_HEIGHT,
                    borderBottom: isLastRow ? 'none' : `1px solid ${theme.palette.divider}`,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <Box
                    sx={{
                      px: 4,
                      py: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      // Frozen permission column — opaque background so the
                      // scrolling role cells don't show through underneath.
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      backgroundColor: theme.palette.background.default,
                      borderRight: PINNED_BORDER,
                    }}
                  >
                    <Typography variant="body2" noWrap>{perm.label}</Typography>
                    {/* Info tooltip — only permissions backfilled with a
                        description show it (§25: the label stays short,
                        the longer explanation lives here). */}
                    {perm.description && (
                      <Tooltip title={perm.description} arrow placement="top">
                        <span style={{ display: 'inline-flex', flexShrink: 0 }}>
                          <InfoIcon
                            strokeWidth={1}
                            size={14}
                            color={theme.palette.grey[600]}
                            style={{ cursor: 'pointer' }}
                          />
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                  {displayRoles.map((role) => {
                    const on = isPermOn(role, perm.name)
                    const editable = editMode
                    return (
                      <Box
                        key={role.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          py: 2,
                        }}
                      >
                        <Switch
                          checked={on}
                          disabled={!editable}
                          onChange={() => handleToggle(role, perm.name)}
                          size="small"
                          sx={{ opacity: editable ? 1 : 0.6 }}
                        />
                      </Box>
                    )
                  })}
                </Box>
              )
            })}
          </Box>
          )
        })}
        {visibleGroups.length === 0 && (
          <Box sx={{ px: 4, py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.disabled">
              No permissions match &quot;{search}&quot;
            </Typography>
          </Box>
        )}
      </Box>
      </Box>

      {/* Reset a Modified built-in role back to global defaults. Decisive action:
          on success it exits edit mode and refetches, so every column reflects
          fresh server state (avoids a stale local edit lingering on the reset
          column). */}
      <ResetRoleModal
        role={resetRole}
        open={!!resetRole}
        onClose={() => setResetRole(null)}
        onReset={() => {
          setResetRole(null)
          cancelEdit()
          refetch()
        }}
      />
    </Box>
  )
}
